/**
 * @module utils/columnSort/shared/dimensionPercentage
 * @description 维度占比配置类型与虚拟列工具（与 columnPercentage 对齐，更新于 2026-06-25）
 */

import type { IColumnSortItem } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';

/**
 * 维度占比配置类型（**与后端契约同构**）
 *
 * 与 columnPercentage 不同：
 * - columnPercentage 是数组（每条 = {metric, table_code}）
 * - dimensionPercentage 是对象 `{ metrics: [{ metric, label, group_by:[...] }] }`
 *   - **group_by 写在每条 metrics[] 内，不在根级**
 *   - 同一 metric 允许多条配置（label / group_by 可不同），对应多个 dim_pct_{metric}_{n}
 */
export interface IDimensionPercentageShape {
  metrics: Array<{
    table_code: string;
    metric: string;
    label: string;
    group_by: Array<{ table_code: string; column: string; }>;
  }>;
}

export function isDimensionPercentageConfig(
  value: unknown,
): value is IDimensionPercentageShape {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** 维度占比虚拟列前缀：与后端 origin_name 规则一致 */
export const DIM_PCT_PREFIX = 'dim_pct_';

/** 构造 dim_pct 虚拟列名（origin_name） */
export function dimPctColumnName(metric: string): string {
  return `${DIM_PCT_PREFIX}${metric}`;
}

/**
 * 构造 dim_pct 虚拟列的展示字段
 * - column: `dim_pct_{metric}`（origin_name）
 * - column_display_cn / en: 使用用户配置 label（若无则回退到 metric 展示名）
 */
export function dimPctColumnForMetric(
  metric: string,
  displayCn: string,
  displayEn: string,
  label?: string,
): Pick<IColumnSortItem, 'column' | 'column_display_cn' | 'column_display_en'> {
  // 后端契约：表头用配置 label；列绑定用 origin_name `dim_pct_{metric}`
  const cn = label?.trim() ? label.trim() : displayCn;
  const en = label?.trim() ? label.trim() : displayEn;
  return {
    column: dimPctColumnName(metric),
    column_display_cn: cn,
    column_display_en: en,
  };
}

/** 是否为 dim_pct 虚拟列 */
export function isDimPctVirtualColumn(item: IColumnSortItem): boolean {
  if (!item?.column) return false;
  return item.column.startsWith(DIM_PCT_PREFIX);
}

/** dim_pct 虚拟列是否绑定到指定源 metric（同 table_code + 物理列名） */
export function isDimPctColumnForMetric(
  item: IColumnSortItem,
  metricColumnName: string,
): boolean {
  if (!isDimPctVirtualColumn(item)) {
    return false;
  }
  const parsed = parseDimPctColumn(item.column || '');
  if (!parsed || parsed.metric !== metricColumnName) {
    return false;
  }
  return true;
}

/** 提取 dim_pct 虚拟列背后的 metric 名 */
export function stripDimPctPrefix(column: string): string {
  if (column.startsWith(DIM_PCT_PREFIX)) {
    return column.slice(DIM_PCT_PREFIX.length);
  }
  return column;
}

/**
 * 构造第 n 条 dim_pct 虚拟列的 origin_name
 * - n=1 → `dim_pct_{metric}`
 * - n>1 → `dim_pct_{metric}_{n}`
 *
 * 与后端契约 §2 origin_name 表一致；用于回写或预生成对齐。
 */
export function dimPctColumnNameAt(metric: string, index: number): string {
  if (index <= 1) return `${DIM_PCT_PREFIX}${metric}`;
  return `${DIM_PCT_PREFIX}${metric}_${index}`;
}

/**
 * 解析 dim_pct 列背后的源 metric + 序号（1-based）
 * - `dim_pct_revenue` → { metric: 'revenue', index: 1 }
 * - `dim_pct_revenue_2` → { metric: 'revenue', index: 2 }
 * - 非 dim_pct_* → null
 */
export function parseDimPctColumn(
  column: string,
): { metric: string; index: number; } | null {
  if (!column || !column.startsWith(DIM_PCT_PREFIX)) return null;
  const rest = column.slice(DIM_PCT_PREFIX.length);
  const m = /^(.*?)(_(\d+))?$/.exec(rest);
  if (!m) return null;
  return {
    metric: m[1] || '',
    index: m[3] ? Number(m[3]) : 1,
  };
}

/**
 * 把每条 metrics[] 配置的（预期）origin_name 列表返回
 *
 * 按 metrics[] 子序列出现顺序分配序号：同 (table_code, metric) 下：
 *   第 1 条 → `dim_pct_{column}`
 *   第 2 条 → `dim_pct_{column}_2`
 *   ...
 *
 * 用于：
 * 1. 首次分析后，将后端 column_names[].origin_name 反查并回写到 metrics[].origin_name
 * 2. 校验配置合法性（与 column_names 对比）
 */
export function expectedDimPctOriginNames(
  metrics: Array<{ table_code?: string; metric?: string; }>,
): Array<{ metric: string; table_code: string; origin_name: string; }> {
  const counters = new Map<string, number>();
  const out: Array<{
    metric: string;
    table_code: string;
    origin_name: string;
  }> = [];
  for (const m of metrics || []) {
    const metric = m?.metric;
    if (!metric) continue;
    const tc = m.table_code || 't0';
    const key = `${tc}::${metric}`;
    const idx = (counters.get(key) || 0) + 1;
    counters.set(key, idx);
    out.push({
      metric,
      table_code: tc,
      origin_name: dimPctColumnNameAt(metric, idx),
    });
  }
  return out;
}
